import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Shuffle, Sparkles, ChevronDown, Search, X, Check, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { useAuth, API } from '../App';
import SubstancePickerInline from '../components/SubstancePickerInline';
import VoiceAvatar from '../components/VoiceAvatar';
import Header from '../components/layout/Header';
import { CatFightThemeSelector } from '../components/DebateConfig';
import { isValidModelId, getInvalidModelVoices, getModelValidationMessage } from '../lib/modelValidation';

const TIER_COLORS = { free: '#A0A0A0', economy: '#22C55E', standard: '#F59E0B', premium: '#EF4444' };
const VOICE_COLORS = ['#EF4444', '#3B82F6', '#22C55E', '#A855F7', '#F59E0B', '#06B6D4', '#EC4899'];

export default function CatFightLaunch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scenario, setScenario] = useState('');
  const [voices, setVoices] = useState([]);
  const [theme, setTheme] = useState('boxing_ring');
  const [availableModels, setAvailableModels] = useState([]);
  const [launching, setLaunching] = useState(false);
  const [topicLoading, setTopicLoading] = useState(false);
  const [error, setError] = useState('');
  const [topicOfDay, setTopicOfDay] = useState(null);
  const [categories, setCategories] = useState([]);
  const [featuredMatchups, setFeaturedMatchups] = useState([]);

  useEffect(() => {
    if (user) {
      API.get('/api/byok/models').then(r => setAvailableModels(r.data.models || [])).catch(() => {});
    }
    API.get('/api/cat-fight/topic-of-the-day').then(r => setTopicOfDay(r.data)).catch(() => {});
    API.get('/api/cat-fight/categories').then(r => setCategories(r.data.categories || [])).catch(() => {});
    API.get('/api/gallery/cat-fights?limit=6&sort=featured').then(r => setFeaturedMatchups(r.data.frays || [])).catch(() => {});
  }, [user]);

  const loadRandomTopic = async (category) => {
    setTopicLoading(true);
    try {
      const url = category ? `/api/cat-fight/random-topic?category=${encodeURIComponent(category)}` : '/api/cat-fight/random-topic';
      const res = await API.get(url);
      setScenario(res.data.topic);
      if (res.data.suggested_voices?.length > 0) {
        setVoices(res.data.suggested_voices.map((sv, i) => ({
          name: sv.name, mandate: sv.position,
          model: 'google/gemini-2.5-flash', model_tier: 'economy',
          color: VOICE_COLORS[i % VOICE_COLORS.length],
        })));
      }
    } catch {}
    setTopicLoading(false);
  };

  const loadSurpriseTopic = async () => {
    setTopicLoading(true);
    try {
      const res = await API.post('/api/cat-fight/generate-topic', {});
      setScenario(res.data.topic);
      if (res.data.suggested_voices?.length > 0) {
        setVoices(res.data.suggested_voices.map((sv, i) => ({
          name: sv.name, mandate: sv.position,
          model: 'google/gemini-2.5-flash', model_tier: 'economy',
          color: VOICE_COLORS[i % VOICE_COLORS.length],
        })));
      }
    } catch {}
    setTopicLoading(false);
  };

  const loadTopicOfDay = () => {
    if (!topicOfDay) return;
    setScenario(topicOfDay.topic);
    if (topicOfDay.suggested_voices?.length > 0) {
      setVoices(topicOfDay.suggested_voices.map((sv, i) => ({
        name: sv.name, mandate: sv.position,
        model: 'google/gemini-2.5-flash', model_tier: 'economy',
        color: VOICE_COLORS[i % VOICE_COLORS.length],
      })));
    }
  };

  const addVoice = () => {
    if (voices.length >= 7) return;
    setVoices(prev => [...prev, {
      name: `Fighter ${prev.length + 1}`, mandate: '',
      model: 'google/gemini-2.5-flash', model_tier: 'economy',
      color: VOICE_COLORS[prev.length % VOICE_COLORS.length],
    }]);
  };

  const removeVoice = (idx) => {
    if (voices.length <= 2) return;
    setVoices(prev => prev.filter((_, i) => i !== idx));
  };

  const updateVoice = (idx, field, value) => {
    setVoices(prev => prev.map((v, i) => i === idx ? { ...v, [field]: value } : v));
  };

  const handleLaunch = async () => {
    if (!user) { navigate('/login'); return; }
    if (!scenario.trim()) { setError('Pick a topic first'); return; }
    if (voices.length < 2) { setError('You need at least 2 fighters'); return; }
    const invalid = getInvalidModelVoices(voices);
    if (invalid.length > 0) { setError(getModelValidationMessage(invalid)); return; }

    setLaunching(true);
    setError('');
    try {
      const res = await API.post('/api/frays', {
        scenario,
        num_phases: 1,
        debate_mode: 'cat_fight',
        friction_level: 10,
        cat_fight_theme: theme,
        custom_voices: voices.map(v => ({
          name: v.name,
          mandate: v.mandate || `Argue passionately as ${v.name}. No holding back.`,
          model: v.model,
          model_tier: v.model_tier,
          color: v.color,
          altered_state: v.altered_state || undefined,
        })),
      });
      navigate(`/fray/${res.data.id}/reveal`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start fight');
      setLaunching(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>

          {/* Header */}
          <div className="mb-8 text-center">
            <span className="text-[10px] font-mono px-2 py-0.5 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444]">ENTERTAINMENT MODE</span>
            <h1 className="text-2xl md:text-4xl font-bold font-['Barlow_Condensed'] mt-3" data-testid="cat-fight-title">
              Cat Fight
            </h1>
            <p className="text-[#A0A0A0] text-base mt-2 max-w-lg mx-auto">
              No-holds-barred AI arguments. Pick a topic, choose your fighters, and heckle from the sidelines.
            </p>
          </div>

          {/* Topic of the Day */}
          {topicOfDay && !scenario && (
            <motion.button
              onClick={loadTopicOfDay}
              className="w-full p-4 mb-6 bg-gradient-to-r from-[#EF4444]/10 to-[#FF4D00]/10 border border-[#EF4444]/30 text-left hover:border-[#EF4444]/60 transition-colors"
              whileHover={{ scale: 1.005 }}
              data-testid="topic-of-day"
            >
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-[#EF4444]" />
                <span className="text-[10px] font-mono text-[#EF4444]">TOPIC OF THE DAY</span>
              </div>
              <p className="text-lg font-semibold font-['Barlow_Condensed'] text-[#F5F5F5]">{topicOfDay.topic}</p>
              <span className="text-[10px] font-mono text-[#A0A0A0] mt-1 block">{topicOfDay.category} — click to load</span>
            </motion.button>
          )}

          {/* Featured Matchups */}
          {featuredMatchups.length > 0 && !scenario && (
            <div className="mb-8" data-testid="featured-matchups">
              <h2 className="text-sm font-mono text-[#EF4444] mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> FEATURED MATCHUPS
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {featuredMatchups.map(f => (
                  <button
                    key={f.id}
                    onClick={() => navigate(`/gallery/cat-fights/${f.id}`)}
                    className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#EF4444]/40 text-left transition-colors min-h-[44px]"
                    data-testid={`featured-matchup-${f.id}`}
                  >
                    <p className="text-sm font-['Barlow_Condensed'] font-medium line-clamp-2 mb-2">{f.scenario || f.template_name}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {(f.voices || []).slice(0, 3).map((v, i) => (
                        <span key={i} className="text-[9px] font-mono px-1.5 py-0.5 border border-[#2A2A2A]" style={{ color: v.color || '#A0A0A0' }}>
                          {v.name}
                        </span>
                      ))}
                    </div>
                    {f.verdict && (
                      <span className={`text-[9px] font-mono mt-2 block ${f.verdict === 'Pass' ? 'text-[#22C55E]' : f.verdict === 'Fail' ? 'text-[#EF4444]' : 'text-[#F59E0B]'}`}>
                        Verdict: {f.verdict}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Topic Selection */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <h2 className="text-lg font-semibold font-['Barlow_Condensed']">Pick Your Fight</h2>
              <div className="flex gap-2">
                <button onClick={() => loadRandomTopic()} disabled={topicLoading}
                  className="px-3 py-1.5 min-h-[44px] bg-[#EF4444] text-white text-xs font-mono flex items-center gap-1.5 disabled:opacity-50 hover:bg-[#EF4444]/80 transition-colors"
                  data-testid="random-topic-btn">
                  <Shuffle className="w-3 h-3" /> {topicLoading ? '...' : 'Random Topic'}
                </button>
                <button onClick={loadSurpriseTopic} disabled={topicLoading}
                  className="px-3 py-1.5 min-h-[44px] border border-[#EF4444]/30 text-[#EF4444] text-xs font-mono flex items-center gap-1.5 disabled:opacity-50 hover:bg-[#EF4444]/10 transition-colors"
                  data-testid="surprise-btn">
                  <Sparkles className="w-3 h-3" /> {topicLoading ? '...' : 'Surprise Me'}
                </button>
              </div>
            </div>

            {/* Category quick picks */}
            {!scenario && categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {categories.map(c => (
                  <button key={c.category} onClick={() => loadRandomTopic(c.category)} disabled={topicLoading}
                    className="px-3 py-1.5 text-[10px] font-mono border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#EF4444] hover:border-[#EF4444]/40 transition-colors disabled:opacity-50"
                    data-testid={`category-${c.category}`}>
                    {c.category} <span className="text-[#2A2A2A] ml-1">{c.count}</span>
                  </button>
                ))}
              </div>
            )}

            <textarea
              value={scenario}
              onChange={e => setScenario(e.target.value)}
              className="w-full h-24 p-4 bg-[#1A1A1A] border border-[#2A2A2A] text-[#F5F5F5] text-sm resize-none focus:outline-none focus:border-[#EF4444] transition-colors"
              placeholder="Or type your own debate topic..."
              data-testid="cat-fight-topic-input"
            />
          </div>

          {/* Fighters */}
          {voices.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold font-['Barlow_Condensed']">Fighters</h2>
                <span className="text-xs font-mono text-[#A0A0A0]">{voices.length} / 7</span>
              </div>
              <div className="space-y-2">
                {voices.map((v, i) => (
                  <div key={i} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center gap-3" data-testid={`fighter-${i}`}>
                    <VoiceAvatar
                      name={v.name || '?'}
                      color={v.color}
                      emotion="idle"
                      alteredState={v.altered_state || null}
                      size={36}
                    />
                    <div className="flex-1 min-w-0">
                      <input type="text" value={v.name} onChange={e => updateVoice(i, 'name', e.target.value)}
                        className="w-full px-2 py-1 bg-transparent border-b border-[#2A2A2A] text-sm font-medium focus:outline-none focus:border-[#EF4444]"
                        data-testid={`fighter-name-${i}`} />
                      <input type="text" value={v.mandate} onChange={e => updateVoice(i, 'mandate', e.target.value)}
                        className="w-full px-2 py-1 bg-transparent text-xs text-[#A0A0A0] focus:outline-none focus:text-[#F5F5F5] mt-1"
                        placeholder="Their position / angle..." data-testid={`fighter-mandate-${i}`} />
                    </div>
                    <SubstancePickerInline
                      selected={v.altered_state || ''}
                      onSelect={(slug) => updateVoice(i, 'altered_state', slug)}
                    />
                    <ModelPickerCompact
                      model={v.model} tier={v.model_tier} models={availableModels}
                      onSelect={(modelId, tier) => updateVoice(i, 'model', modelId) || updateVoice(i, 'model_tier', tier)}
                      index={i}
                    />
                    {voices.length > 2 && (
                      <button onClick={() => removeVoice(i)} className="p-1 text-[#A0A0A0] hover:text-[#EF4444]">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {voices.length < 7 && (
                <button onClick={addVoice}
                  className="w-full mt-2 py-2 border border-dashed border-[#2A2A2A] text-xs text-[#A0A0A0] hover:text-[#EF4444] hover:border-[#EF4444]/40 transition-colors flex items-center justify-center gap-1"
                  data-testid="add-fighter-btn">
                  <Plus className="w-3 h-3" /> Add Fighter
                </button>
              )}
            </div>
          )}

          {/* No voices yet prompt */}
          {voices.length === 0 && scenario && (
            <div className="mb-8 p-4 bg-[#1A1A1A] border border-[#2A2A2A] text-center">
              <p className="text-sm text-[#A0A0A0] mb-3">Topic loaded — now add fighters or use Random Topic to get suggested voices.</p>
              <button onClick={addVoice}
                className="px-4 py-2 bg-[#EF4444] text-white text-xs font-mono" data-testid="add-first-fighter">
                <Plus className="w-3 h-3 inline mr-1" /> Add Fighters Manually
              </button>
            </div>
          )}

          {/* Theme */}
          <div className="mb-8">
            <CatFightThemeSelector value={theme} onChange={setTheme} />
          </div>

          {/* Launch */}
          {error && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm" data-testid="cat-fight-error">{error}</div>}

          <div className="p-4 md:p-6 bg-[#1A1A1A] border border-[#EF4444]/20">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-lg font-semibold font-['Barlow_Condensed'] mb-1">Start the Fight</h3>
                <p className="text-xs text-[#A0A0A0] font-mono">
                  {voices.length} fighters — friction MAXIMUM — runs until you stop
                </p>
              </div>
              <button onClick={handleLaunch}
                disabled={launching || !scenario.trim() || voices.length < 2}
                className="w-full md:w-auto px-8 py-3 min-h-[44px] bg-[#EF4444] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#EF4444]/90 transition-colors disabled:opacity-40"
                data-testid="launch-cat-fight-btn">
                {launching ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Starting...</>
                ) : (
                  <><Zap className="w-4 h-4" /> FIGHT!</>
                )}
              </button>
            </div>
          </div>

        </motion.div>
      </div>
    </div>
  );
}


function ModelPickerCompact({ model, tier, models, onSelect, index }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = models.filter(m =>
    !search || m.id.toLowerCase().includes(search.toLowerCase()) || m.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="relative shrink-0" ref={ref}>
      <button onClick={() => setOpen(!open)}
        className={`px-2 py-1 text-[9px] font-mono border transition-colors ${open ? 'border-[#EF4444] text-[#EF4444]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:text-[#EF4444]'}`}
        data-testid={`fighter-model-${index}`}>
        <span style={{ color: TIER_COLORS[tier] }}>{tier?.slice(0, 4).toUpperCase()}</span>
        <ChevronDown className="w-3 h-3 inline ml-1" />
      </button>
      {open && (
        <div className="absolute z-50 right-0 mt-1 w-64 sm:w-72 bg-[#1A1A1A] border border-[#2A2A2A] shadow-xl max-h-60 flex flex-col">
          <div className="p-2 border-b border-[#2A2A2A]">
            <div className="relative">
              <Search className="w-3 h-3 text-[#A0A0A0] absolute left-2 top-1/2 -translate-y-1/2" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-7 pr-2 py-1 bg-[#0F0F0F] border border-[#2A2A2A] text-[10px] focus:outline-none focus:border-[#EF4444]"
                placeholder="Search..." autoFocus />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.slice(0, 30).map(m => (
              <button key={m.id} onClick={() => { onSelect(m.id, m.tier); setOpen(false); setSearch(''); }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-[10px] border-b border-[#0F0F0F] ${model === m.id ? 'bg-[#EF4444]/10' : 'hover:bg-[#2A2A2A]/50'}`}>
                <span className="w-10 text-center px-1 py-0.5 border font-mono shrink-0"
                  style={{ color: TIER_COLORS[m.tier], borderColor: TIER_COLORS[m.tier] + '40', fontSize: '8px' }}>
                  {m.tier?.toUpperCase()?.slice(0, 4)}
                </span>
                <span className="truncate text-[#F5F5F5]">{m.name || m.id}</span>
                {model === m.id && <Check className="w-3 h-3 text-[#EF4444] shrink-0 ml-auto" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
